import {Fragment} from 'react'
import {isBefore, isToday, startOfToday} from 'date-fns'
import {
  BoardColumn,
  BoardEntry,
  formatSlotRange,
  isAllDay,
  ISO_DAY_LABELS,
  slotChipStyle,
  SLOT_TYPE_LABELS,
} from '../helpers/availability'
import {TableGridTimeline} from '../styles/styles'

/** One employee, one row. */
export interface BoardRow {
  employeeId: number
  name: string
  position: string
}

interface Props {
  columns: BoardColumn[]
  rows: BoardRow[]
  /** employee_id → the entries of each column, in column order (`buildBoardRow`). */
  entries: Map<number, BoardEntry[][]>
  use12Hour?: boolean
  disabled?: boolean
  onCellClick: (employeeId: number, column: number) => void
}

const ROW_MIN_HEIGHT = 58
const HEADER_MIN_HEIGHT = 50

// TableGridTimeline paints .rs__cell from MUI's palette (white) and draws its
// borders in MUI grey — both wrong under data-bs-theme=dark. Re-point them at
// the Bootstrap variables, scoped to this board so the schedule grid, which
// shares the component, is untouched. (Same fix as AvailabilityGrid.)
const themedGrid = {
  borderColor: 'var(--bs-border-color-translucent)',
  '& .rs__cell': {
    background: 'var(--bs-body-bg)',
    borderColor: 'var(--bs-border-color-translucent)',
  },
} as const

const columnLabel = (column: BoardColumn) => {
  const day = ISO_DAY_LABELS[column.isoDay]
  if (!column.date) return day
  return `${day} ${column.date.toLocaleDateString(undefined, {day: 'numeric', month: 'short'})}`
}

/** What one entry reads as, in a cell and in a cell's aria-label alike. */
const entryLabel = (entry: BoardEntry, use12Hour: boolean) =>
  `${SLOT_TYPE_LABELS[entry.slot.slot_type]} · ${
    isAllDay(entry.slot) ? 'all day' : formatSlotRange(entry.slot, use12Hour)
  }`

/**
 * The roster-wide availability board: employees down, days across, a whole day
 * per cell — the same shape as the weekly schedule, so the two can be read
 * side by side.
 *
 * Unlike the paint grid this never edits in place: a cell is a list of what is
 * registered for that day, and clicking it opens the modal that owns the
 * editing. That is what makes the all-employees layout possible at all — 7 ×
 * 48 half-hour buttons per employee would be thousands of cells, and a drag
 * across a roster has no meaning.
 *
 * Faded entries are the standing weekly pattern showing through a dated
 * column, with the hours the dated entries cover already cut out of it. They
 * are deliberately *in* the cell rather than hidden: a manager looking at next
 * Monday needs to see the rule that still applies to it, and the fade is what
 * says "this came from the pattern, not from this date".
 */
export const AvailabilityBoard = ({
  columns,
  rows,
  entries,
  use12Hour = false,
  disabled,
  onCellClick,
}: Props) => (
  <>
    <TableGridTimeline hours={columns.length} sticky='1' sx={themedGrid}>
      <span
        className='rs__cell rs__header rs__time bg-light text-gray-900'
        style={{minHeight: HEADER_MIN_HEIGHT}}
      >
        <span className='fs-8 fw-semibold text-uppercase text-gray-600'>Team member</span>
      </span>
      {columns.map((column, index) => (
        <span
          key={index}
          className={`rs__cell rs__header rs__time bg-light text-gray-900 ${
            column.date && isToday(column.date) ? 'rs__today_cell' : ''
          }`}
          style={{minHeight: HEADER_MIN_HEIGHT}}
        >
          <span className='d-flex flex-column align-items-center lh-1 py-2'>
            <span className='fw-bold fs-7'>{ISO_DAY_LABELS[column.isoDay]}</span>
            {column.date && (
              // The date is what distinguishes one Monday from the next, so it
              // is not decoration — it goes under the name at full legibility.
              <span
                className='fs-8 fw-semibold text-gray-600 mt-1'
                data-testid={`availability-board-date-${index}`}
              >
                {column.date.toLocaleDateString(undefined, {day: 'numeric', month: 'short'})}
              </span>
            )}
          </span>
        </span>
      ))}
    </TableGridTimeline>

    <TableGridTimeline hours={columns.length} sx={themedGrid}>
      {rows.map((row) => {
        const rowEntries = entries.get(row.employeeId)
        return (
          <Fragment key={row.employeeId}>
            <span
              className='rs__cell rs__header rs__resource bg-light'
              style={{minHeight: ROW_MIN_HEIGHT}}
            >
              <span className='d-flex flex-column lh-1 py-2' style={{width: '100%'}}>
                <span className='fs-7 fw-semibold text-gray-900'>{row.name}</span>
                {row.position && (
                  <span className='fs-8 text-gray-600 mt-1 text-uppercase'>{row.position}</span>
                )}
              </span>
            </span>

            {columns.map((column, index) => {
              const cellEntries = rowEntries?.[index] ?? []
              // A date already past cannot be written: the API refuses it
              // outright (`replace_exceptions` raises on date_from < today),
              // so the cell shows what was registered and takes no clicks —
              // better than a click that comes back as a failed save. The
              // current week always has such days in it from Tuesday on, which
              // is why this is not just a matter of the week arrows.
              const past = !!column.date && isBefore(column.date, startOfToday())
              const label = `${row.name} — ${columnLabel(column)}`
              const content = cellEntries.length
                ? cellEntries
                    .map(
                      (entry) =>
                        `${entryLabel(entry, use12Hour)}${entry.inherited ? ' (weekly rule)' : ''}`
                    )
                    .join('; ')
                : 'nothing set — available'
              const title = `${label}: ${content}${past ? ' — in the past, cannot be changed' : ''}`

              return (
                <div key={index} className='rs__cell' style={{minHeight: ROW_MIN_HEIGHT}}>
                  <button
                    type='button'
                    data-testid={`availability-board-cell-${row.employeeId}-${index}`}
                    aria-label={title}
                    title={title}
                    disabled={disabled || past}
                    data-past={past ? 'true' : undefined}
                    onClick={() => onCellClick(row.employeeId, index)}
                    style={{
                      width: '100%',
                      height: '100%',
                      minHeight: ROW_MIN_HEIGHT,
                      padding: '3px',
                      border: 0,
                      background: 'transparent',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                      alignItems: 'stretch',
                      justifyContent: cellEntries.length ? 'flex-start' : 'center',
                      cursor: disabled || past ? 'default' : 'pointer',
                      // A past day is history, not a greyed-out control: its
                      // entries stay legible, the cell just does not invite a
                      // click.
                      opacity: past ? 0.6 : 1,
                    }}
                  >
                    {cellEntries.length ? (
                      cellEntries.map((entry, entryIndex) => (
                        <span
                          key={entryIndex}
                          // The fill is built from CSS variables, which jsdom
                          // drops on parse — this is the only handle a test
                          // (or a bug report) has on which layer an entry is.
                          data-inherited={entry.inherited ? 'true' : undefined}
                          data-slot-type={entry.slot.slot_type}
                          className='d-block rounded px-2 py-1 text-start text-truncate'
                          style={{
                            // The chip carries its own light surface and black
                            // text, so it reads the same in both themes. The
                            // inherited one is paled by a veil over the colour
                            // rather than by opacity — opacity would fade the
                            // words with the fill.
                            ...slotChipStyle(entry.slot.slot_type, entry.inherited),
                            border: entry.inherited
                              ? '1px dashed rgba(0, 0, 0, 0.35)'
                              : '1px solid transparent',
                          }}
                        >
                          <span className='fs-8 fw-bold'>
                            {isAllDay(entry.slot)
                              ? 'All day'
                              : formatSlotRange(entry.slot, use12Hour)}
                          </span>
                          <span className='fs-8 ms-2'>
                            {SLOT_TYPE_LABELS[entry.slot.slot_type]}
                          </span>
                        </span>
                      ))
                    ) : (
                      // An empty cell has to say it can be clicked: without
                      // this the board reads as a static report of the days
                      // that DO have entries. A past day is offered nothing,
                      // because nothing can be added to it.
                      <span className='fs-8 text-gray-400' aria-hidden='true'>
                        {past ? (
                          '—'
                        ) : (
                          <>
                            <i className='bi bi-plus-lg' /> Add
                          </>
                        )}
                      </span>
                    )}
                  </button>
                </div>
              )
            })}
          </Fragment>
        )
      })}
    </TableGridTimeline>
  </>
)
